import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './HandsScrollAnimation.css';

const FRAME_COUNT = 240;

const HandsScrollAnimation = () => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [images, setImages] = useState([]);
    const [loadedFrames, setLoadedFrames] = useState(0);
    const [buttonOpacity, setButtonOpacity] = useState(0);
    const [buttonPointerEvents, setButtonPointerEvents] = useState('none');

    useEffect(() => {
        const loadedImages = [];
        for (let i = 1; i <= FRAME_COUNT; i++) {
            const img = new Image();
            const frameNum = i.toString().padStart(3, '0');
            img.src = `/hands-holding/ezgif-frame-${frameNum}.jpg`;
            img.onload = () => {
                setLoadedFrames(prev => prev + 1);
            };
            loadedImages.push(img);
        }
        setImages(loadedImages);
    }, []);

    useEffect(() => {
        if (images.length === 0) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');

        const drawInitial = () => {
            if (images[0] && images[0].complete) {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
                drawImageProp(context, images[0], canvas.width, canvas.height);
            } else {
                setTimeout(drawInitial, 100);
            }
        };
        drawInitial();

        const handleScroll = () => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            // Using a more stable height reference for mobile
            const containerHeight = containerRef.current.scrollHeight;
            const viewportHeight = window.innerHeight;
            
            // Calculate scroll progress within the component's range
            const currentScroll = window.scrollY;
            const containerOffsetTop = rect.top + currentScroll;
            const scrollDistance = currentScroll - containerOffsetTop;
            const maxScrollable = containerHeight - viewportHeight;

            let scrollFraction = 0;
            if (scrollDistance > 0) {
                scrollFraction = Math.min(1, scrollDistance / maxScrollable);
            } else if (scrollDistance > maxScrollable) {
                scrollFraction = 1;
            }

            const frameIndex = Math.min(
                FRAME_COUNT - 1,
                Math.floor(scrollFraction * FRAME_COUNT)
            );

            requestAnimationFrame(() => {
                if (images[frameIndex] && images[frameIndex].complete) {
                    canvas.width = window.innerWidth;
                    canvas.height = window.innerHeight;
                    drawImageProp(context, images[frameIndex], canvas.width, canvas.height);
                }

                // Smooth opacity for button
                let opacity = 0;
                if (scrollFraction > 0.88) {
                    opacity = Math.min(1, (scrollFraction - 0.88) / 0.12);
                }
                setButtonOpacity(opacity);
                setButtonPointerEvents(opacity > 0.8 ? 'auto' : 'none');
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll);
        // Initial draw
        handleScroll();
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [images]);

    return (
        <div className="hands-animation-container" ref={containerRef} id="hands-animation">
            <div className="canvas-wrapper">
                <canvas ref={canvasRef} />

                <div
                    className="interactive-events-layer"
                    style={{ opacity: buttonOpacity, pointerEvents: buttonPointerEvents }}
                >
                    <Link to="/brochure" className="styled-events-btn">
                        <span>BROCHURE</span>
                        <div className="btn-glow"></div>
                    </Link>
                </div>

                {loadedFrames < FRAME_COUNT && (
                    <div className="loading-indicator">
                        <div className="loader-dots">
                             <span>.</span><span>.</span><span>.</span>
                        </div>
                        Loading Dhwani '26... {Math.floor((loadedFrames / FRAME_COUNT) * 100)}%
                    </div>
                )}
            </div>
        </div>
    );
};

function drawImageProp(ctx, img, canvasWidth, canvasHeight) {
    // Clear the canvas first
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Fill with black background
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Immersive Landscape "Cover" Scaling logic
    const imgRatio = img.width / img.height;
    const canvasRatio = canvasWidth / canvasHeight;

    let drawWidth, drawHeight, x, y;

    if (canvasRatio > imgRatio) {
        // Window is wider than image: fill width
        drawWidth = canvasWidth;
        drawHeight = canvasWidth / imgRatio;
        x = 0;
        y = (canvasHeight - drawHeight) / 2;
    } else {
        // Window is taller than image (Mobile/Portrait): fill height
        drawHeight = canvasHeight;
        drawWidth = canvasHeight * imgRatio;
        x = (canvasWidth - drawWidth) / 2;
        y = 0;
    }

    ctx.drawImage(img, x, y, drawWidth, drawHeight);
}

export default HandsScrollAnimation;
